import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { gsap } from 'gsap';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

const SQUARE_LINKS = {
  starter: "https://square.link/u/HMxTh8Vw",
  creatorMonthly: "https://square.link/u/fa1iCLWL",
  creatorAnnual: "https://square.link/u/m46OyUxC",
  proMonthly: "https://square.link/u/o25DM4sO",
  proAnnual: "https://square.link/u/JcndaVXT"
};

// ---------- Bundle‑centric Plan Caps (exact iterations, zero‑out bundles) ----------
const ITERATIONS_PER_FINAL_IMAGE = 10;

const PLAN_CAPS = {
  Starter:   { iterations: 77,  videos: 2,  finalImages: 7 },
  Creator:   { iterations: 342, videos: 12, finalImages: 34 },
  Pro:       { iterations: 765, videos: 27, finalImages: 76 },
};

const FREELANCE_RATE_IMAGE = 35;      // per final image
const VIDEO_RATE_LOW = 75;            // per motion clip
const VIDEO_RATE_HIGH = 150;          // per motion clip

// Target bundles that zero out the generation budget
const TARGET_BUNDLES = {
  Starter: { videos: 1, images: 5 },
  Creator: { videos: 7, images: 15 },
  Pro:     { videos: 14, images: 38 },
};

function getBundleValueRange(planName) {
  const b = TARGET_BUNDLES[planName];
  if (!b) return { low: 0, high: 0, desc: '' };
  const low = b.videos * VIDEO_RATE_LOW + b.images * FREELANCE_RATE_IMAGE;
  const high = b.videos * VIDEO_RATE_HIGH + b.images * FREELANCE_RATE_IMAGE;
  const desc = `${b.videos} video${b.videos !== 1 ? 's' : ''} + ${b.images} final image${b.images !== 1 ? 's' : ''}`;
  return { low, high, desc };
}

function getImageOnlyValue(planName) {
  const caps = PLAN_CAPS[planName];
  if (!caps) return 0;
  return caps.finalImages * FREELANCE_RATE_IMAGE;
}

function formatValueRange(low, high) {
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return `${formatter.format(low)} – ${formatter.format(high)}`;
}
// --------------------------------------------------------------------------

export default function Pricing() {
  useEffect(() => {
    document.title = "Outlne Pricing | Interior Design Editor Plans & Free Trial";
  }, []);

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(null);
  const [hoveredPlanIndex, setHoveredPlanIndex] = useState(null);
  const [flippedIndex, setFlippedIndex] = useState(null);

  const lastGridChangeTime = useRef(0);
  const containerRef = useRef(null);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);
  const threeMountRef = useRef(null);
  const plansContainerRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  const manualAdjustments = {
    desktop: {
      headerMarginTop: '0px',
      headerMarginBottom: '3rem',
      toggleMarginBottom: '3rem',
      plansGap: '2.5rem',
      creditMarginTop: '1.5rem',
      creditMarginBottom: '2rem',
    },
    tablet: {
      headerMarginTop: '0px',
      headerMarginBottom: '2rem',
      toggleMarginBottom: '2rem',
      plansGap: '2rem',
      creditMarginTop: '1rem',
      creditMarginBottom: '1.5rem',
    },
    mobile: {
      headerMarginTop: '30px',
      headerMarginBottom: '1.5rem',
      toggleMarginBottom: '1.5rem',
      plansGap: '1.5rem',
      creditMarginTop: '1rem',
      creditMarginBottom: '1.5rem',
    }
  };

  // Feature lists – exact iteration caps, bundle info, no social claims
  const starterFeatures = [
    "3‑day free trial",
    "77 iterations",
    "2 videos max (7‑15s each)",
    "Supports up to: 1 short motion clip + 5 final images",
    "Up to 7 final images (at ~10 iterations each)",
    "HD resolution exports (1080p)",
    "Basic style suggestions",
    "No watermark on exports",
    "Export up to 5 design projects",
    "Video export (H.264, .MOV)",
    "Email support within 48 hours"
  ];

  const creatorExclusiveFeatures = [
    "342 iterations per month",
    "12 videos max (7‑15s each)",
    "Supports up to: 7 motion clips + 15 final images",
    "Up to 34 final images (at ~10 iterations each)",
    "4K resolution exports",
    "Advanced style suggestions",
    "Precision perspective matching",
    "Commercial usage rights",
    "3D model import capabilities",
    "Unlimited design projects",
    "Video export (H.264, .MOV, ProRes)",
    "Priority email support within 24 hours",
    "Usage limits reset each billing cycle"
  ];

  const proExclusiveFeatures = [
    "765 iterations per month",
    "27 videos max (7‑15s each)",
    "Supports up to: 14 motion clips + 38 final images",
    "Up to 76 final images (at ~10 iterations each)",
    "8K resolution exports",
    "Advanced 3D model manipulation",
    "Batch export processing",
    "Client presentation tools",
    "Dedicated priority support",
    "Early access to new features",
    "Personalized recommendations",
    "Video export (H.264, .MOV, ProRes, RAW)"
  ];

  const plans = {
    monthly: [
      {
        name: "Starter",
        desc: "3‑day free trial. Complete access to our Interior Design Editor tools.",
        price: "$0",
        features: starterFeatures,
        cta: "Start Free Trial",
        squareLink: SQUARE_LINKS.starter,
        planType: "starter",
        billingCycle: "trial"
      },
      {
        name: "Creator",
        desc: "For renters, homeowners, and designers decorating apartments and personal spaces.",
        price: "$45",
        features: [
          ...creatorExclusiveFeatures
        ],
        cta: "Choose Creator",
        squareLink: SQUARE_LINKS.creatorMonthly,
        planType: "creator",
        billingCycle: "monthly"
      },
      {
        name: "Pro",
        desc: "For anyone who wants the ultimate Interior Design tool with pro‑level features.",
        price: "$100",
        features: [
          "All previous plan features",
          ...proExclusiveFeatures
        ],
        cta: "Choose Pro",
        squareLink: SQUARE_LINKS.proMonthly,
        planType: "pro",
        billingCycle: "monthly"
      }
    ],
    annual: [
      {
        name: "Starter",
        desc: "3‑day free trial. Complete access to our Interior Design editor tools.",
        price: "$0",
        features: starterFeatures,
        cta: "Start Free Trial",
        squareLink: SQUARE_LINKS.starter,
        planType: "starter",
        billingCycle: "trial"
      },
      {
        name: "Creator",
        desc: "For renters, homeowners, and designers decorating apartments and personal spaces.",
        price: "$450",
        saving: "$90",
        features: [
          "All previous plan features",
          ...creatorExclusiveFeatures
        ],
        cta: "Choose Creator",
        squareLink: SQUARE_LINKS.creatorAnnual,
        planType: "creator",
        billingCycle: "annual"
      },
      {
        name: "Pro",
        desc: "For anyone who wants the ultimate Interior Design tool with pro‑level features.",
        price: "$1,000",
        saving: "$200",
        features: [
          "All previous plan features",
          ...proExclusiveFeatures
        ],
        cta: "Choose Pro",
        squareLink: SQUARE_LINKS.proAnnual,
        planType: "pro",
        billingCycle: "annual"
      }
    ]
  };

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      const timeSinceChange = time - lastGridChangeTime.current;
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  const handlePlanClick = (plan) => {
    sessionStorage.setItem('selectedPlan', JSON.stringify({
      name: plan.name,
      price: plan.price,
      planType: plan.planType,
      billingCycle: plan.billingCycle,
      saving: plan.saving || null,
      squareLink: plan.squareLink
    }));
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const popup = window.open(
      '/auth',
      'Outlne Sign Up',
      `width=${width},height=${height},left=${left},top=${top}`
    );
    if (popup) popup.focus();
  };

  const scrollToPlan = (index) => {
    setCurrentPlanIndex(index);
    setSelectedPlanIndex(index);
    if (plansContainerRef.current) {
      const planCards = plansContainerRef.current.children;
      if (planCards[index]) {
        const container = plansContainerRef.current;
        const card = planCards[index];
        const containerWidth = container.offsetWidth;
        const cardWidth = card.offsetWidth;
        const scrollLeft = card.offsetLeft - (containerWidth / 2) + (cardWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  };

  const handlePlanSelect = (index) => {
    setSelectedPlanIndex(index);
  };

  const handlePlanHover = (index) => {
    setHoveredPlanIndex(index);
  };

  const handlePlanLeave = () => {
    setHoveredPlanIndex(null);
  };

  const toggleFlip = (index) => {
    setFlippedIndex(prev => prev === index ? null : index);
  };

  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  const getAdjustments = () => {
    if (isMobile) return manualAdjustments.mobile;
    if (isTablet) return manualAdjustments.tablet;
    return manualAdjustments.desktop;
  };

  const adjustments = getAdjustments();

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  // Flip side content helper – shows bundle value range and image‑only fallback
  const getFlipContent = (plan, billingCycle) => {
    const { name } = plan;
    const caps = PLAN_CAPS[name];
    const bundleValue = getBundleValueRange(name);
    const imageOnly = getImageOnlyValue(name);
    const annualMultiplier = billingCycle === 'annual' ? 12 : 1;

    const lowAnnual = bundleValue.low * annualMultiplier;
    const highAnnual = bundleValue.high * annualMultiplier;
    const imageOnlyAnnual = imageOnly * annualMultiplier;

    const suffix = billingCycle === 'annual' ? '/year' : '/month';

    const priceNum = parseFloat(plan.price.replace(/[$,]/g, ''));
    const midPoint = (lowAnnual + highAnnual) / 2;
    const roi = priceNum > 0 ? Math.round(midPoint / priceNum) : null;

    return {
      displayValRange: formatValueRange(lowAnnual, highAnnual),
      imageOnlyVal: imageOnlyAnnual,
      suffix,
      bundleDesc: bundleValue.desc,
      caps,
      roi
    };
  };

  const infoIconStyle = {
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 'bold',
    color: 'white',
    lineHeight: 1,
    marginLeft: '8px',
    verticalAlign: 'middle',
    transition: 'background-color 0.2s ease',
  };

  return (
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(29, 29, 29, 0.8)', zIndex: -3 }} />
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -2, overflow: 'hidden', willChange: 'transform', opacity: bgOpacity, transition: 'opacity 1s ease' }}>
        <div style={{ position: 'absolute', top: '-10%', left: '-10%', right: '-10%', bottom: '-10%', backgroundImage: `url(${backgrounds[bgIndex]})`, backgroundSize: 'cover', backgroundPosition: 'center', transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`, mixBlendMode: 'soft-light', transition: 'background-image 1s ease-in-out, transform 8s linear', willChange: 'transform, opacity', backfaceVisibility: 'hidden', transformStyle: 'preserve-3d' }} />
      </div>
      <div style={{ position: 'fixed', top: 0, left: 0, width: isMobile ? '100vw' : '50vw', bottom: 0, zIndex: -1, overflow: 'hidden', opacity: gridOpacity, transition: 'opacity 1s ease' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: isMobile ? '100vw' : '200vw', maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)', WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)' }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: `linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)`, ...style }} />
          ))}
        </div>
      </div>

      {/* Header / Logo */}
      {!isMobile && (
        <>
          <div style={{ position: 'fixed', top: '30px', left: '30px', zIndex: 20, cursor: 'pointer', fontSize: '2rem', color: 'white' }} onClick={() => navigate('/')}>×</div>
          <div style={{ position: 'fixed', top: '40px', right: '40px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: 'auto' }} />
              <h1 style={{ fontSize: '1.8rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
            </div>
            <p style={{ fontSize: '1rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 8px', borderRadius: '4px', display: 'inline-block', minWidth: '60px', textAlign: 'center' }}>Editor</span>
            </p>
          </div>
        </>
      )}
      {isMobile && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '110px', backgroundColor: 'rgba(31, 31, 31, 0.8)', backdropFilter: 'blur(10px)', zIndex: 15 }} />
          <div style={{ position: 'fixed', top: '20px', left: '20px', zIndex: 20, textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', letterSpacing: '-1.2px', margin: 0, color: 'white', fontWeight: 500 }}>Outlne</h1>
            <p style={{ fontSize: '0.9rem', margin: '5px 0 0 0', color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              Interior Design <span style={{ backgroundColor: '#ffffff', color: 'black', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', minWidth: '50px', textAlign: 'center' }}>Editor</span>
            </p>
          </div>
          <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 20, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
            <img src={LogoWhite} alt="Outlne Logo" style={{ width: '40px', height: '40px' }} />
            <div style={{ cursor: 'pointer', marginRight: '10px', fontSize: '1.8rem', color: 'white', marginTop: '-5px' }} onClick={() => navigate('/')}>×</div>
          </div>
        </>
      )}

      {/* Navigation arrows (desktop) */}
      {!isMobile && (
        <>
          <button onClick={() => scrollToPlan((currentPlanIndex - 1 + plans[billingCycle].length) % plans[billingCycle].length)}
            style={{ position: 'fixed', left: '20px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.3)', color: 'white', borderRadius: '4px', width: '35px', height: '55px', cursor: 'pointer', zIndex: 10, fontSize: '28px', paddingLeft: '9px', paddingTop: '3px', transition: 'all 0.3s ease' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}>‹</button>
          <button onClick={() => scrollToPlan((currentPlanIndex + 1) % plans[billingCycle].length)}
            style={{ position: 'fixed', right: '20px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.3)', color: 'white', borderRadius: '4px', width: '35px', height: '55px', cursor: 'pointer', zIndex: 10, fontSize: '28px', paddingLeft: '9px', paddingTop: '3px', transition: 'all 0.3s ease' }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}>›</button>
        </>
      )}

      {/* Main content */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 5, overflowY: 'auto', overflowX: 'hidden', opacity: pageLoaded ? 1 : 0, transition: 'opacity 1s ease', overscrollBehavior: 'none' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', minHeight: isMobile ? 'auto' : '100vh', paddingTop: isMobile ? '100px' : isTablet ? '60px' : '70px', paddingBottom: '0', position: 'relative', zIndex: 5 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: isMobile ? '100%' : isTablet ? '700px' : '1500px', padding: isMobile ? '0 20px' : '0 40px', flex: '1' }}>
            <div style={{ textAlign: 'center', width: '100%', marginTop: adjustments.headerMarginTop, marginBottom: adjustments.headerMarginBottom }}>
              <h1 style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: isMobile ? '1.6rem' : isTablet ? '1.8rem' : '2.2rem', fontWeight: '500', color: 'white', marginBottom: '0.8rem', lineHeight: '1.2', letterSpacing: '-0.5px' }}>Transform Any Space</h1>
              <p style={{ color: '#ccc', fontSize: isMobile ? '0.9rem' : isTablet ? '0.95rem' : '1.1rem', lineHeight: '1.5', fontFamily: 'avenir-lt-pro, sans-serif', margin: 0 }}>
                Elevate your Interior Design workflow.
              </p>
              {/* New residential link */}
              <div style={{ marginTop: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#46a6ffff', fontSize: '0.85rem', cursor: 'pointer',  }} onClick={() => navigate('/residential')}>
                  Now Offering Amenity Services
                </span>
                <button
                  onClick={() => navigate('/residential')}
                  style={{
                    backgroundColor: '#ffffffff',
                    color: '#333232ff',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '2px 10px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    lineHeight: '1.4'
                  }}
                >Go</button>
              </div>
            </div>

            {/* Billing toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: adjustments.toggleMarginBottom, padding: '0.5rem', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '50px', border: '1px solid rgba(255, 255, 255, 0.1)', maxWidth: '300px', width: '100%' }}>
              <span style={{ color: billingCycle === 'monthly' ? 'white' : '#888', padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'monthly' ? '600' : '400', cursor: 'pointer' }} onClick={() => setBillingCycle('monthly')}>Monthly</span>
              <div style={{ width: '40px', height: '24px', borderRadius: '12px', backgroundColor: billingCycle === 'monthly' ? '#333' : '#40a3ff', position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px', transition: 'all 0.3s ease' }} onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}>
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', position: 'absolute', left: billingCycle === 'monthly' ? '2px' : '18px', transition: 'left 0.3s ease' }} />
              </div>
              <span style={{ color: billingCycle === 'annual' ? 'white' : '#888', padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'annual' ? '600' : '400', cursor: 'pointer' }} onClick={() => setBillingCycle('annual')}>Annual</span>
            </div>

            {/* Plans container */}
            <div ref={plansContainerRef} style={{
              display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isTablet ? 'center' : 'center', alignItems: isMobile ? 'center' : 'stretch',
              gap: adjustments.plansGap, width: '100%', overflow: isMobile ? 'visible' : 'visible', padding: isMobile ? '0' : '0 10px',
              transform: isTablet ? 'scale(0.85)' : 'scale(1)', transformOrigin: 'center top', transition: 'transform 0.3s ease', marginBottom: adjustments.creditMarginTop,
            }}>
              {plans[billingCycle].map((plan, index) => {
                const isFlipped = flippedIndex === index;
                const flipContent = getFlipContent(plan, billingCycle);
                const isMobileStarter = isMobile && plan.name === 'Starter';

                return (
                  <div key={index}
                    onClick={() => handlePlanSelect(index)}
                    onMouseEnter={() => handlePlanHover(index)}
                    onMouseLeave={handlePlanLeave}
                    style={{
                      backgroundColor: 'rgba(47, 47, 47, 0.9)',
                      border: selectedPlanIndex === index ? '2px solid #40a3ff' : hoveredPlanIndex === index ? '2px solid rgba(64, 163, 255, 0.7)' : '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '1rem',
                      padding: isMobileStarter ? '1rem 1.5rem' : isMobile ? '1.5rem' : isTablet ? '1.5rem' : '2rem',
                      width: isMobile ? '90%' : isTablet ? '300px' : '100%',
                      maxWidth: isMobile ? '100%' : isTablet ? '300px' : '380px',
                      backdropFilter: 'blur(15px)', WebkitBackdropFilter: 'blur(15px)',
                      display: 'flex', flexDirection: 'column',
                      height: isMobileStarter ? (isFlipped ? '640px' : '480px') : 'auto',
                      overflow: isMobileStarter ? 'hidden' : 'visible',
                      minHeight: isMobile ? 'auto' : isTablet ? '550px' : '600px',
                      flex: isMobile ? '0 0 auto' : isTablet ? '0 0 auto' : '1',
                      scrollSnapAlign: isTablet ? 'center' : 'none',
                      margin: isMobile ? '0 auto' : '0',
                      flexShrink: 0,
                      transition: 'border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
                      transform: selectedPlanIndex === index || hoveredPlanIndex === index ? 'translateY(-5px)' : 'none',
                      boxShadow: selectedPlanIndex === index ? '0 10px 30px rgba(64, 163, 255, 0.2)' : hoveredPlanIndex === index ? '0 5px 15px rgba(64, 163, 255, 0.1)' : 'none',
                      position: 'relative',
                    }}
                  >
                    {/* Title + info icon */}
                    <h2 style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: isMobile ? '1.4rem' : isTablet ? '1.4rem' : '1.6rem', fontWeight: '600', color: 'white', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                      {plan.name}
                      <span onClick={(e) => { e.stopPropagation(); toggleFlip(index); }}
                        style={infoIconStyle}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}>
                        i
                      </span>
                    </h2>

                    {/* Flippable content area */}
                    {isMobileStarter ? (
                      <div style={{ display: 'grid', gridTemplateRows: '1fr', flex: 1, position: 'relative' }}>
                        {/* Front side */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          opacity: isFlipped ? 0 : 1,
                          visibility: isFlipped ? 'hidden' : 'visible',
                          pointerEvents: isFlipped ? 'none' : 'auto',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <p style={{ color: '#ccc', fontSize: isMobile ? '0.9rem' : isTablet ? '0.9rem' : '1rem', marginBottom: isMobileStarter ? '0.8rem' : isMobile ? '1rem' : isTablet ? '1rem' : '1.5rem', lineHeight: '1.5' }}>{plan.desc}</p>
                            <div style={{ marginBottom: isMobileStarter ? '0.8rem' : isMobile ? '1rem' : isTablet ? '1rem' : '1.5rem' }}>
                              <p style={{ fontSize: isMobileStarter ? '1.8rem' : isMobile ? '2rem' : isTablet ? '2rem' : '2.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
                                {plan.price}<span style={{ fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1.1rem', fontWeight: 'normal' }}>/mo</span>
                              </p>
                              {billingCycle === 'annual' && plan.saving && (
                                <p style={{ color: '#40a3ff', fontSize: '0.85rem', margin: '0.5rem 0 0 0' }}>Save {plan.saving} annually</p>
                              )}
                              {billingCycle === 'annual' && plan.name !== 'Starter' && (
                                <p style={{ color: '#888', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>Billed annually at {plan.price}</p>
                              )}
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: isMobileStarter ? '0 0 0.8rem 0' : '0 0 1.5rem 0', color: 'white', fontSize: isMobileStarter ? '0.85rem' : isMobile ? '0.9rem' : isTablet ? '0.85rem' : '1rem', whiteSpace: 'pre-line' }}>
                              {plan.features.map((feature, fIdx) => {
                                const isHeader = feature.startsWith('All ');
                                return (
                                  <li key={fIdx} style={{
                                    marginBottom: isMobileStarter ? '0.3rem' : '0.5rem',
                                    paddingLeft: isHeader ? '0' : '1.5rem',
                                    position: 'relative',
                                    lineHeight: '1.4',
                                    fontWeight: isHeader ? 600 : 400,
                                    color: isHeader ? '#40a3ff' : 'white',
                                    fontSize: isHeader ? '0.8rem' : 'inherit',
                                    textTransform: isHeader ? 'uppercase' : 'none',
                                    letterSpacing: isHeader ? '0.5px' : '0',
                                  }}>
                                    {!isHeader && (
                                      <span style={{ position: 'absolute', left: 0, color: '#40a3ff', fontWeight: 'bold' }}>✓</span>
                                    )}
                                    {feature}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                            style={{
                              width: '100%',
                              marginTop: 'auto',
                              padding: isMobileStarter ? '10px 18px' : isMobile ? '12px 20px' : isTablet ? '12px 24px' : '14px 28px',
                              backgroundColor: selectedPlanIndex === index ? '#0081f9ff' : 'white',
                              color: selectedPlanIndex === index ? 'white' : '#000',
                              border: 'none', borderRadius: '50px', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '600',
                              fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1rem',
                              cursor: 'pointer', transition: 'all 0.3s ease',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = selectedPlanIndex === index ? '#2d8cff' : '#f0f0f0'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedPlanIndex === index ? '#40a3ff' : 'white'}
                          >{plan.cta}</button>
                        </div>

                        {/* Back side (flipped) */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          opacity: isFlipped ? 1 : 0,
                          visibility: isFlipped ? 'visible' : 'hidden',
                          pointerEvents: isFlipped ? 'auto' : 'none',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <p style={{ color: '#40a3ff', fontSize: isMobileStarter ? '0.85rem' : '0.9rem', fontWeight: 600, margin: '0 0 0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Real-World Value
                          </p>
                          <p style={{ color: '#ddd', fontSize: isMobileStarter ? '0.9rem' : '0.95rem', lineHeight: 1.5, margin: '0 0 1.2rem' }}>
                            Outlne lets you explore endless design iterations via manual area selection: swap furniture, paint, change materials, and more. <br></br><br></br> Once the user has perfected a room, they then export it as a final image or turn it into a short motion clip.
                          </p>
                          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '0.8rem', padding: isMobileStarter ? '0.8rem' : '1rem', marginBottom: '1.2rem' }}>
                            <p style={{ fontSize: isMobileStarter ? '1.3rem' : '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
                              {flipContent.displayValRange}
                              <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>{flipContent.suffix}</span>
                            </p>
                            <p style={{ color: '#bbb', fontSize: '0.8rem', margin: '0.3rem 0 0', lineHeight: 1.4 }}>
                              What a professional photographer & videographer would charge for the same final content output.
                            </p>
                            <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '0.8rem 0 0', color: '#ccc', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              <li>Short‑form motion clip (7–15s): $75 – $150 each</li>
                              <li>Final interior image: $35 each</li>
                              <li style={{ marginTop: '0.3rem', color: '#40a3ff' }}>
                                Bundle: {flipContent.bundleDesc}
                              </li>
                              {flipContent.caps && (
                                <li style={{ marginTop: '0.3rem' }}>
                                  Image‑only fallback: {flipContent.caps.finalImages} images → {formatter.format(flipContent.imageOnlyVal)}
                                </li>
                              )}
                            </ul>
                            {flipContent.roi && (
                              <p style={{ color: '#40a3ff', fontSize: '0.8rem', margin: '0.6rem 0 0', fontWeight: 600 }}>
                                ≈ {flipContent.roi}x your plan cost (mid‑range estimate)
                              </p>
                            )}
                          </div>

                          <div style={{ marginTop: 'auto', width: '100%' }}>
                            <p
                              onClick={(e) => { e.stopPropagation(); toggleFlip(index); }}
                              style={{ color: '#40a3ff', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '0.5rem', textAlign: 'center' }}
                            >Back to Plan Features</p>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                              style={{
                                width: '100%',
                                padding: isMobileStarter ? '10px 18px' : isMobile ? '12px 20px' : isTablet ? '12px 24px' : '14px 28px',
                                backgroundColor: '#ffffff', color: '#000', border: 'none', borderRadius: '50px',
                                fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '600',
                                fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1rem',
                                cursor: 'pointer', transition: 'all 0.3s ease',
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >{plan.cta}</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateRows: '1fr', flex: 1 }}>
                        {/* Front side */}
                        <div style={{
                          gridRow: 1, gridColumn: 1,
                          opacity: isFlipped ? 0 : 1,
                          visibility: isFlipped ? 'hidden' : 'visible',
                          pointerEvents: isFlipped ? 'none' : 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%'
                        }}>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <p style={{ color: '#ccc', fontSize: isMobile ? '0.9rem' : isTablet ? '0.9rem' : '1rem', marginBottom: isMobileStarter ? '0.8rem' : isMobile ? '1rem' : isTablet ? '1rem' : '1.5rem', lineHeight: '1.5' }}>{plan.desc}</p>
                            <div style={{ marginBottom: isMobileStarter ? '0.8rem' : isMobile ? '1rem' : isTablet ? '1rem' : '1.5rem' }}>
                              <p style={{ fontSize: isMobileStarter ? '1.8rem' : isMobile ? '2rem' : isTablet ? '2rem' : '2.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
                                {plan.price}<span style={{ fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1.1rem', fontWeight: 'normal' }}>/mo</span>
                              </p>
                              {billingCycle === 'annual' && plan.saving && (
                                <p style={{ color: '#40a3ff', fontSize: '0.85rem', margin: '0.5rem 0 0 0' }}>Save {plan.saving} annually</p>
                              )}
                              {billingCycle === 'annual' && plan.name !== 'Starter' && (
                                <p style={{ color: '#888', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>Billed annually at {plan.price}</p>
                              )}
                            </div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: isMobileStarter ? '0 0 0.8rem 0' : '0 0 1.5rem 0', color: 'white', fontSize: isMobileStarter ? '0.85rem' : isMobile ? '0.9rem' : isTablet ? '0.85rem' : '1rem', whiteSpace: 'pre-line' }}>
                              {plan.features.map((feature, fIdx) => {
                                const isHeader = feature.startsWith('All ');
                                return (
                                  <li key={fIdx} style={{
                                    marginBottom: isMobileStarter ? '0.3rem' : '0.5rem',
                                    paddingLeft: isHeader ? '0' : '1.5rem',
                                    position: 'relative',
                                    lineHeight: '1.4',
                                    fontWeight: isHeader ? 600 : 400,
                                    color: isHeader ? '#40a3ff' : 'white',
                                    fontSize: isHeader ? '0.8rem' : 'inherit',
                                    textTransform: isHeader ? 'uppercase' : 'none',
                                    letterSpacing: isHeader ? '0.5px' : '0',
                                  }}>
                                    {!isHeader && (
                                      <span style={{ position: 'absolute', left: 0, color: '#40a3ff', fontWeight: 'bold' }}>✓</span>
                                    )}
                                    {feature}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                            style={{
                              width: '100%',
                              marginTop: 'auto',
                              padding: isMobileStarter ? '10px 18px' : isMobile ? '12px 20px' : isTablet ? '12px 24px' : '14px 28px',
                              backgroundColor: selectedPlanIndex === index ? '#0081f9ff' : 'white',
                              color: selectedPlanIndex === index ? 'white' : '#000',
                              border: 'none', borderRadius: '50px', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '600',
                              fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1rem',
                              cursor: 'pointer', transition: 'all 0.3s ease',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = selectedPlanIndex === index ? '#2d8cff' : '#f0f0f0'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = selectedPlanIndex === index ? '#40a3ff' : 'white'}
                          >{plan.cta}</button>
                        </div>

                        {/* Back side (flipped) */}
                        <div style={{
                          gridRow: 1, gridColumn: 1,
                          opacity: isFlipped ? 1 : 0,
                          visibility: isFlipped ? 'visible' : 'hidden',
                          pointerEvents: isFlipped ? 'auto' : 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%'
                        }}>
                          <p style={{ color: '#40a3ff', fontSize: isMobileStarter ? '0.85rem' : '0.9rem', fontWeight: 600, margin: '0 0 0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Real-World Value
                          </p>
                          <p style={{ color: '#ddd', fontSize: isMobileStarter ? '0.9rem' : '0.95rem', lineHeight: 1.5, margin: '0 0 1.2rem' }}>
                            Outlne lets you explore endless design iterations via manual area selection: swap furniture, paint, change materials, and more. <br></br><br></br> Once the user has perfected a room, they then export it as a final image or turn it into a short motion clip.
                          </p>
                          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '0.8rem', padding: isMobileStarter ? '0.8rem' : '1rem', marginBottom: '1.2rem' }}>
                            <p style={{ fontSize: isMobileStarter ? '1.3rem' : '1.5rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
                              {flipContent.displayValRange}
                              <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>{flipContent.suffix}</span>
                            </p>
                            <p style={{ color: '#bbb', fontSize: '0.8rem', margin: '0.3rem 0 0', lineHeight: 1.4 }}>
                              What a professional photographer & videographer would charge for the same final content output.
                            </p>
                            <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '0.8rem 0 0', color: '#ccc', fontSize: '0.8rem', lineHeight: 1.5 }}>
                              <li>Short‑form motion clip (7–15s): $75 – $150 each</li>
                              <li>Final interior image: $35 each</li>
                              <li style={{ marginTop: '0.3rem', color: '#40a3ff' }}>
                                Bundle: {flipContent.bundleDesc}
                              </li>
                              {flipContent.caps && (
                                <li style={{ marginTop: '0.3rem' }}>
                                  Image‑only fallback: {flipContent.caps.finalImages} images → {formatter.format(flipContent.imageOnlyVal)}
                                </li>
                              )}
                            </ul>
                            {flipContent.roi && (
                              <p style={{ color: '#40a3ff', fontSize: '0.8rem', margin: '0.6rem 0 0', fontWeight: 600 }}>
                                ≈ {flipContent.roi}x your plan cost (mid‑range estimate)
                              </p>
                            )}
                          </div>

                          <div style={{ marginTop: 'auto', width: '100%' }}>
                            <p
                              onClick={(e) => { e.stopPropagation(); toggleFlip(index); }}
                              style={{ color: '#40a3ff', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '0.5rem', textAlign: 'center' }}
                            >Back to Plan Features</p>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePlanClick(plan); }}
                              style={{
                                width: '100%',
                                padding: isMobileStarter ? '10px 18px' : isMobile ? '12px 20px' : isTablet ? '12px 24px' : '14px 28px',
                                backgroundColor: '#ffffff', color: '#000', border: 'none', borderRadius: '50px',
                                fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '600',
                                fontSize: isMobileStarter ? '0.9rem' : isMobile ? '1rem' : isTablet ? '0.9rem' : '1rem',
                                cursor: 'pointer', transition: 'all 0.3s ease',
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >{plan.cta}</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ textAlign: 'center', width: '80%', marginTop: isTablet ? '1rem' : '1rem', marginBottom: adjustments.creditMarginBottom, padding: '0 16px' }}>
              <p style={{ color: '#888', fontSize: isMobile ? '0.75rem' : isTablet ? '0.75rem' : '0.85rem', fontFamily: 'avenir-lt-pro, sans-serif', margin: 0, maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
                <strong style={{ color: '#aaa' }}>Usage limits:</strong> Generation caps reset each billing cycle. Image and video allowances are as stated on the selected plan.
              </p>
            </div>
          </div>
          <div style={{ width: '100%', position: 'relative', zIndex: 5, backgroundColor: '#1a1a1a' }}>
            <Footer />
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes blink { 0%, 100% { opacity: 0.7; } 50% { opacity: 0.2; } }
          html { overflow: hidden; height: 100%; width: 100%; position: fixed; }
          body { overflow: hidden; height: 100%; width: 100%; position: fixed; user-select: none; -webkit-user-select: none; -moz-user-select: none; margin: 0; padding: 0; overscroll-behavior: none; -webkit-overflow-scrolling: touch; }
          #root { height: 100%; width: 100%; overflow: hidden; position: fixed; }
          * { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
          div[style*="mask-image"] { pointer-events: none; }
        `}
      </style>
    </>
  );
}